/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { css } from "@panda/css";
import { VStack } from "@panda/jsx";

import { CosmosRichText } from "../../../atoms/rich-text/CosmosRichText.atom";
import { CosmosText } from "../../../atoms/text/CosmosText.atom";

export interface DirectoryHeadingProps {
	eyebrow?: string | null;
	title?: string | null;
	/**
	 * Description rendered as HTML via `CosmosRichText`. The `Html` suffix
	 * signals that the value is treated as rich-text HTML (matches the
	 * Hygraph CKEditor field shape), NOT escaped text — callers passing a
	 * non-CMS string should sanitize first.
	 */
	descHtml?: string | null;
}

// Eyebrow → title → desc gap. `eds.dimension.16` matches Cosmos
// `SectionHeading` / `title-desc-stack` convention.
const headingStackStyles = css({
	width: "full",
	maxWidth: "eds.dimension.860",
	// Between the heading block and the filter row — matches BlogCards' canonical
	// `eds.dimension.48` heading-to-content gap (40 on smDown).
	paddingBlockEnd: "eds.dimension.48",
	smDown: { paddingBlockEnd: "eds.dimension.40" },
});

const descStyles = css({
	color: "eds.text.secondary",
});

/**
 * Directory grid heading slot — eyebrow + title + description above the
 * filter row. Returns `null` if none of the three are set so consumers can
 * pass empty values through the CMS without leaving an empty block.
 *
 * `descHtml` is rendered via `CosmosRichText` because the Hygraph field
 * uses a CKEditor renderer — values come back as HTML strings.
 */
export function DirectoryHeading({ eyebrow, title, descHtml }: DirectoryHeadingProps): React.JSX.Element | null {
	if (!eyebrow && !title && !descHtml) return null;
	return (
		<VStack className={headingStackStyles} gap="eds.dimension.16" alignItems="flex-start">
			{eyebrow ? <CosmosText variant="eyebrowMD">{eyebrow}</CosmosText> : null}
			{title ? (
				<CosmosText variant="headingLG" as="h2">
					{title}
				</CosmosText>
			) : null}
			{descHtml ? (
				<div className={descStyles}>
					<CosmosRichText html={descHtml} />
				</div>
			) : null}
		</VStack>
	);
}
